/**
 * Thinking block — live-only Codex-style inline marker.
 *
 * Renders as a single inline marker plus a rolling, lightly-styled
 * preview of the current thinking text. No card border, no wide
 * pill, no leading icon — the medium-weight verb + dim duration is
 * the whole affordance.
 *
 * Thinking blocks tick a per-second counter while live, then freeze
 * until the next visible event/text/tool row replaces them. That
 * gives the chat a responsive "the agent is thinking" marker without
 * duplicating durable transcript content.
 *
 * Long thinking blocks render inside a fixed three-line viewport
 * that stays scrolled to the bottom, so new thoughts push older
 * lines out of view FIFO-style without growing the transcript
 * height unbounded.
 */

import * as React from 'react';
import type { BlockTiming } from '@/lib/agent/reducer';
import type { ThinkingBlock } from '@/lib/agent/types';
import { cn } from '@/lib/utils/cn';
import { formatElapsed } from './elapsed';
import { useSteadyRevealText } from './useSteadyRevealText';

export interface ThinkingBlockViewProps {
  block: ThinkingBlock;
  timing?: BlockTiming;
  /** True only for the currently growing renderable block in an uncommitted assistant turn. */
  streaming?: boolean;
  /** Notify the owning scroll container that the live preview changed height. */
  onLayoutShift?: () => void;
  className?: string;
}

export const ThinkingBlockView = React.memo(function ThinkingBlockView({
  block,
  timing,
  streaming = false,
  onLayoutShift,
  className,
}: ThinkingBlockViewProps) {
  const text = (block.thinking ?? '').trim();
  const hasBody = text.length > 0;

  const live = streaming && !!timing && timing.endedAt === undefined;
  const elapsedMs = useThinkingElapsed(timing, live);
  const label = `${live ? 'Thinking' : 'Thought'} for ${formatElapsed(elapsedMs)}`;

  const visibleText = useSteadyRevealText(text, live);
  const previewText = thinkingPreviewTail(visibleText);
  const previewRef = React.useRef<HTMLDivElement>(null);

  // Scroll the preview to its bottom on every revealed-text change
  // and notify the owning scroll container that our height changed.
  // The first non-empty ``previewText`` is also when ``hasBody``
  // flips and the open transition begins, so the same effect covers
  // the "preview is opening" and "preview is growing inside" cases.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll the preview only when revealed thinking text changes
  React.useLayoutEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    onLayoutShift?.();
  }, [previewText, onLayoutShift]);

  if (!timing) return null;

  // Render label as two parts — verb and the duration — so the
  // duration sits in the same dim color as the row's prose tail
  // and the verb carries the slight emphasis (matches the tool row
  // exactly: medium-weight verb + dim continuation).
  const verb = label.split(/\s/)[0];
  const tail = label.slice(verb.length).trimStart();

  // The preview slot is ALWAYS in the tree so the very first
  // delta doesn't pop a fresh ~20px tall block into the column
  // (the "thinking blocks pop in then expand" jitter). Instead
  // the slot transitions ``grid-template-rows`` from ``0fr`` to
  // ``1fr`` -- the canonical CSS-only height-from-0-to-auto
  // pattern -- so it grows in smoothly and the column height
  // never changes in a single frame. ``max-h`` still caps the
  // visible body at three lines once the preview is open.
  return (
    <div className={cn('group/think select-none text-[14px]', className)}>
      <div className="-mx-1 inline-flex max-w-full items-baseline rounded px-1 py-px text-left text-gray-600 dark:text-gray-300">
        {/* Single flex-item wrapper so "Thinking for 7s" copies as
            one line instead of "Thinking\nfor 7s" — the browser
            inserts a newline between every flex item when text is
            copied. See ``ToolUseBlockView`` for the same pattern. */}
        <span className="min-w-0 truncate">
          <span className="font-medium text-gray-600 dark:text-gray-300">{verb}</span>
          {tail && (
            <>
              {' '}
              <span className="text-gray-400 dark:text-gray-500">{tail}</span>
            </>
          )}
        </span>
      </div>
      <div
        aria-hidden={!hasBody}
        className={cn(
          'ml-1 grid max-w-[min(46rem,100%)] transition-[grid-template-rows,opacity,margin-top] duration-200 ease-out',
          hasBody ? 'mt-1 grid-rows-[1fr] opacity-100' : 'mt-0 grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <div
            ref={previewRef}
            className="max-h-[3.75rem] select-text overflow-hidden border-gray-200/70 border-l pl-3 text-[13px] text-gray-400/95 italic leading-5 dark:border-white/10 dark:text-gray-500"
          >
            <div className="whitespace-pre-wrap break-words">{previewText}</div>
          </div>
        </div>
      </div>
    </div>
  );
});

function thinkingPreviewTail(text: string): string {
  const trimmed = text.trim();
  const maxChars = 2000;
  if (trimmed.length <= maxChars) return trimmed;

  const tail = trimmed.slice(-maxChars);
  const lineBreak = tail.indexOf('\n');
  if (lineBreak >= 0 && lineBreak < 240) return tail.slice(lineBreak + 1).trimStart();
  const wordBreak = tail.indexOf(' ');
  return wordBreak >= 0 ? tail.slice(wordBreak + 1).trimStart() : tail;
}

/**
 * Tick the duration once a second while the block is live, then
 * freeze on the recorded ``startedAt`` → ``endedAt`` delta. Returns
 * 0 when no timing was ever captured (history-loaded blocks).
 */
function useThinkingElapsed(timing: BlockTiming | undefined, live: boolean): number {
  const [, force] = React.useReducer((n: number) => n + 1, 0);

  React.useEffect(() => {
    if (!live) return;
    const id = window.setInterval(force, 1000);
    return () => window.clearInterval(id);
  }, [live]);

  if (!timing) return 0;
  if (live) {
    return Math.max(0, Date.now() - timing.startedAt);
  }
  if (timing.endedAt !== undefined) {
    return Math.max(0, timing.endedAt - timing.startedAt);
  }
  return 0;
}
